/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/no-explicit-any */
import isMobilePhone from "validator/lib/isMobilePhone";
import * as z from "zod";

/** Represents a form field with its properties */
interface Field {
  name: string;
  fieldType: string;
  required?: boolean;
  min?: number;
  max?: number;
  requiredError?: string;
  minError?: string;
  maxError?: string;
}

/** Represents a row of fields in a form */
interface Row {
  fields: Field[];
}

/** Represents a group of rows or a special field group in a form */
interface Group {
  groupType: string;
  rows?: Row[];
  requiredError?: string;
}

export const schemaValidator = {
  firstNameRegex: /^[\u00C0-\u017F\w\s.,'-]{1,40}$/,
  lastNameRegex: /^[\u00C0-\u017F\w\s.,'-]{1,80}$/,
  companyRegex: /^[\u00C0-\u017F\w\s.,'-]{0,255}$/,
  phoneRegex: /^\+?\d[\d\s\-()]{7,25}$/,
  faxRegex: /^\+?\d[\d\s\-()]{6,39}$/,
  Street: /^[\u00C0-\u017F\w\s.,'#\-]{1,120}$/,
  City: /^[\w\s\-]{1,255}$/,
  Lead_State__c: /^[\w\s\-]{1,80}$/,
  PostalCode: /^[\w\s\-]{3,20}$/,
  emailRegex: /^[\w.\-+]{1,32}@[\w.-]{4,32}$/,
  site_Zip__cRegex: /^[A-Za-z\d\s\-]{0,12}$/,
  siteZipRegex: /^[A-Za-z\d\s\-]{0,12}$/,
  Quote_Id__c: /^[\w\-]{1,38}$/,
  Title: /^[\w\s.,'&\-]{1,128}$/,
  comments__cRegex: /^[\u00C0-\u017F\w\s.,'"\-!?@$#&*]{0,32000}$/,
  Site_Address__c: /^[\w\s.,'#\-]{1,120}$/,
  dataNameRegex: /^[\w\s.-]{0,64}$/,
  dataValueRegex: /^[\w\s.-]{0,64}$/,
  campaignRegex: /^[\w\s'-]{0,64}$/,
  debugsourceRegex: /^[\w\s-]{0,32}$/,
  isdisplayAdRegex: /^(true|false)$/,
  sendDebugEmailRegex: /^(true|false)$/,
  industryRegex: /^[\w\s\/-]{0,32}$/,
  ownerIdRegex: /^[\w-]{0,32}$/,
  requester__cRegex: /^[\w-]{0,32}$/,
  typeRegex: /^[\d]{0,12}$/,
  GA_Campaign__cRegex: /^[\w\s.-]{0,64}$/,
  GA_Content__cRegex: /^[\w\s.-]{0,64}$/,
  GA_Medium__cRegex: /^[\w\s.-]{0,64}$/,
  GA_Source__cRegex: /^[\w\s.-]{0,64}$/,
  GA_Term__cRegex: /^[\w\s.-]{0,64}$/,
  GCLID__c: /^[\w\-]{1,64}$/,

  LeadQA_Product_Requested__c: /^[\w\s.,'"\-!?@$#&*]{0,32000}$/,
  LeadQA_Quantity_Requested__c: /^[\w\s.,\-]{1,255}$/,
  LeadQA_Jobsite_Essentials__c: /^[\w\s.,\-]{1,255}$/,
  LeadQA_Number_of_People_Using__c: /^\d{1,3}$/,
  LeadQA_Product_Usage__c: /^[\w\s.,\-]{1,255}$/,
  LeadQA_Lease_Term__c: /^[\w\s.,\-]{1,255}$/,
  LeadQA_Requested_Delivery_Date__cRegex: /^[\w\s\-/:.]{0,255}$/,
  LeadQA_Additional_Information__c: /^[\w\s.,'"\-!?@$#&*]{0,32000}$/,
  LeadQA_USS_Branch__c: /^[\w\s\-]{1,255}$/,
  LeadQA_USS_Product_Location__c: /^[\w\s.,\-]{1,255}$/,
  LeadQA_USS_Product_Name__c: /^[\w\s.,\-]{1,255}$/,
  LeadQA_USS_Sales_Price__c: /^\$?\d{1,9}(\.\d{2})?$/,
  LeadQA_USS_Unit_Number__cRegex: /^[\w\-]{1,255}$/,
};

/**
 * Creates a dynamic Zod schema based on the provided data groups.
 * @param dataGroups - An array of Group objects representing the form structure
 * @returns A record of Zod schemas for each field
 */
export const createDynamicSchemaSupportThirdTab = (
  dataGroups: Group[],
  textFields?: any
): Record<string, z.ZodTypeAny> => {
  const res: Record<string, z.ZodTypeAny> = {};

  let invoiceField: Field;

  dataGroups.forEach((group) => {
    if (group.groupType === "fieldGroupOptionCards") {
      res.cards = z.string().min(1, { message: group.requiredError });
      return;
    }

    group.rows?.forEach((row) => {
      row.fields?.forEach((field) => {
        if (field.name === "PAI_Invoice") {
          invoiceField = field;
        }
        res[field.name] = createFieldSchema(field);
      });
    });
  });

  if (textFields?.length > 0) {
    textFields.forEach((field: { name: string | number }) => {
      res[field.name] = createFieldSchema(invoiceField);
    });
  }

  return res;
};

export const createDynamicSchemaColumnForm = (fields: any, zipName?: string) => {
  const res: Record<string, z.ZodTypeAny> = {};

  (fields || []).forEach((field: any) => {
    field.fieldType = field.itemType;
    res[field.name] = createFieldSchema(field, zipName);
  });

  return res;
};

/**
 * Creates a Zod schema for a specific field based on its type.
 * @param field - The field object containing field properties
 * @returns A Zod schema for the field
 */
const createFieldSchema = (field: Field, zipName?: string): z.ZodTypeAny => {
  switch (field.fieldType) {
    case "fieldCalendar":
      return createCalendarSchema(field);
    case "fieldPhone":
      return createPhoneSchema(field);
    case "fieldText":
      return createTextSchema(field, zipName);
    case "fieldGoogleAddress":
      return createGoogleAddressSchema(field);
    case "fieldDropdown":
      return createDropdownSchema(field);
    case "fieldEmail":
      return createEmailSchema(field);
    case "fieldTextArea":
      return createTextAreaSchema(field);
    default:
      return z.any();
  }
};

const createCalendarSchema = (field: Field): z.ZodTypeAny => {
  return z.date({ invalid_type_error: field.requiredError });
};

const createPhoneSchema = (field: Field): z.ZodTypeAny => {
  const baseSchema = z.string().max(256).refine(isMobilePhone, { message: "Invalid phone number" });
  return field.required ? baseSchema : baseSchema.optional().or(z.literal(""));
};

const createTextSchema = (field: Field, zipName?: string): z.ZodTypeAny => {
  if (field.name === "Your best offer") {
    return z.coerce
      .number({ invalid_type_error: "Price must be a number" })
      .refine((val) => `${val}`.length <= (field.max || 20), field.maxError || "Maximum 20 characters")
      .optional();
  }

  if (field.name === "First Name*") {
    return z
      .string()
      .min(field.min || 3, { message: field.minError || field.requiredError })
      .regex(new RegExp(schemaValidator.firstNameRegex), {
        message: "Please enter valid First name.",
      });
  }
  if (field.name === "Last Name*") {
    return z
      .string()
      .min(field.min || 3, { message: field.minError || field.requiredError })
      .regex(new RegExp(schemaValidator.lastNameRegex), {
        message: "Please enter valid Last name.",
      });
  }
  if (field.name === "Company Name") {
    return z.string().regex(new RegExp(schemaValidator.companyRegex), {
      message: "Please enter valid Company name.",
    });
  }

  if (field.name === zipName) {
    return z
      .string()
      .min(field.min || 3, { message: field.minError || field.requiredError })
      .max(field.max || 10, { message: field.maxError || "Maximum 10 characters" });
  }

  if (field.name.endsWith("_zip")) {
    // return z.string().optional();
    return z
      .string()
      .min(field.min || 3, { message: field.minError || field.requiredError })
      .max(field.max || 10, { message: field.maxError || "Maximum 10 characters" });
  }

  const baseSchema = z.string().max(field.max || 256, { message: field.maxError || "Maximum 256 characters" });
  return field.required
    ? baseSchema.min(field.min || 2, { message: field.minError || field.requiredError })
    : baseSchema.optional();
};

const createGoogleAddressSchema = (field: Field): z.ZodTypeAny => {
  return z
    .string()
    .min(field.min || 2, { message: field.minError || field.requiredError })
    .max(field.max || 256, { message: field.maxError || "Maximum 256 characters" });
};

const createDropdownSchema = (field: Field): z.ZodTypeAny => {
  const baseSchema = z.union([
    z.array(z.string()).min(1, { message: field.requiredError || "At least one option must be selected" }),
    z.string().min(1, { message: field.requiredError || "Required" }),
  ]);

  return field.required ? baseSchema : baseSchema.optional();
};

const createEmailSchema = (field: Field): z.ZodTypeAny => {
  const baseSchema = z
    .string()
    .max(256)
    .email({ message: field.requiredError || "Invalid email address" });
  return field.required ? baseSchema : z.string().max(0).or(baseSchema);
};

const createTextAreaSchema = (field: Field): z.ZodTypeAny => {
  const baseSchema = z.string().max(field.max || 131072, { message: field.maxError || "Maximum 131072 characters" });
  return field.required
    ? baseSchema.min(field.min || 1, { message: field.minError || field.requiredError })
    : baseSchema.optional();
};
